// ============================================================================
// Kategorie beim Zuordnen in den Vario-Kategoriebaum bringen (listing-status.ts).
//
// Anlass (2026-09-13): Aufschalten uebernimmt die Kaufland-Kategorie des Katalogtreffers und
// schreibt sie ans Vario-Listing. Dafuer muss sie als Knoten im Vario-Baum der Storefront
// stehen, und den fuellt nur der Worker-Job `kaufland.push_categories_to_vario` -- mit genau
// den Kategorien aus der Auswahl des Haendlers (`kaufland.tenant_categories`). Eine Kategorie,
// die nur im Kaufland-Katalog vorkommt, scheiterte deshalb mit CATEGORY_NOT_PUSHED_TO_VARIO,
// und niemand stiess den Sync an. Der Slot nimmt sie jetzt auf (Frage: nur dieser Kanal oder
// alle aktiven), startet den Sync, wartet und wiederholt die Zuweisung.
//
// Hier liegen die DOM- und Netz-freien Teile: Fehlercode-Erkennung, Ziel-Kanaele fuer den
// Push, Fragetext. Die RPC-Aufrufe und das Warten stehen im Slot.
// ============================================================================

import type { VererbungsErgebnis } from './category-modal.js';

/** Die beiden 409-Codes von `vario-listing-category` (action=assign), die ein Sync heilt. */
export type KategorieSyncFehler = 'CATEGORY_NOT_PUSHED_TO_VARIO' | 'TREE_NOT_PUSHED';

const SYNC_FEHLER: ReadonlyArray<KategorieSyncFehler> = ['CATEGORY_NOT_PUSHED_TO_VARIO', 'TREE_NOT_PUSHED'];

/**
 * Erkennt den Fehlercode am ANFANG der Meldung (`callFn` formt `"<code>: <message>"`) oder als
 * nackten Code. Ein Code mitten in einer fremden Meldung zaehlt nicht: Nur die Function selbst
 * darf die Reparatur ausloesen, nicht ein Text, der den Code zufaellig zitiert.
 */
export function erkenneKategorieSyncFehler(fehler: string | null | undefined): KategorieSyncFehler | null {
  if (typeof fehler !== 'string') return null;
  const kopf = fehler.trim().split(':')[0]?.trim() ?? '';
  return SYNC_FEHLER.find((c) => c === kopf) ?? null;
}

/** Was der Haendler auf die Frage antwortet. */
export type KanalWahl = 'alle' | 'nur-diese';

/**
 * Kanaele, fuer die nach der Aufnahme ein Sync gestartet wird: die Quelle immer, dazu jeder
 * Kanal, in dem das Vererben etwas NEU aufgenommen hat (`added > 0`). `added 0` heisst: dort
 * war die Kategorie schon manuell drin (dann stand sie beim letzten Sync bereits im Baum) oder
 * der Katalog des Kanals kennt sie nicht (dann gaebe es nichts zu pushen).
 */
export function zielKanaeleFuerPush(quelle: string, vererbt: VererbungsErgebnis | null | undefined): string[] {
  const q = quelle.trim().toLowerCase();
  const ziele = [q];
  for (const [roh, erg] of Object.entries(vererbt ?? {})) {
    const code = roh.trim().toLowerCase();
    if (code === '' || ziele.includes(code)) continue;
    if ((erg?.added ?? 0) > 0) ziele.push(code);
  }
  return ziele;
}

/** Eine Zeile der Auswahl, wie `ladeGewaehlteKategorien` sie liefert. */
export interface AuswahlZeile {
  categoryId: number;
  addedBy: 'manual' | 'auto';
}

/**
 * ID-Liste fuer `kaufland_set_tenant_categories(_storefront, ids, 'manual')`.
 *
 * Die RPC ist ein ABGLEICH, kein Hinzufuegen: Bei `'manual'` loescht sie jede manuelle Zeile
 * der Storefront, die nicht in der Liste steht (Migration 0043). Wer nur die neue ID
 * uebergibt, loescht damit die gesamte gepflegte Auswahl des Haendlers (Review-Fund
 * 2026-09-13). Deshalb: alle bestehenden manuellen Zeilen plus die neue. `auto`-Zeilen werden
 * nicht mitgegeben, sonst wuerden sie zu `manual` hochgestuft; sie bleiben von der Abwahl
 * ohnehin unberuehrt.
 */
export function kategorieIdsFuerAufnahme(gewaehlt: ReadonlyArray<AuswahlZeile>, neu: number): number[] {
  const ids = gewaehlt.filter((g) => g.addedBy === 'manual').map((g) => g.categoryId);
  if (!ids.includes(neu)) ids.push(neu);
  return ids;
}

/** Ergebniszeile von `kaufland_enqueue_category_push` (RETURNS TABLE, PostgREST liefert ein Array). */
export interface PushZeile {
  jobId: string | null;
  enqueued: boolean;
  reason: string | null;
}

/** Normalisiert die RPC-Antwort: Array (Normalfall), einzelnes Objekt oder nichts. */
export function pushZeile(data: unknown): PushZeile | null {
  const roh = Array.isArray(data) ? data[0] : data;
  if (!roh || typeof roh !== 'object') return null;
  const r = roh as Record<string, unknown>;
  return {
    jobId: typeof r['job_id'] === 'string' && r['job_id'].length > 0 ? r['job_id'] : null,
    enqueued: r['enqueued'] === true,
    reason: typeof r['reason'] === 'string' ? r['reason'] : null,
  };
}

/**
 * Lesbarer Text fuer die bekannten Fehler der Aufnahme-RPC. `unknown category_id in list`
 * heisst: der lokale Kaufland-Katalog dieser Storefront kennt die ID nicht (Cache veraltet
 * oder Crawl nie gelaufen), nicht dass die Kategorie bei Kaufland fehlt.
 */
export function aufnahmeFehlerText(message: string, storefront: string): string {
  if (/unknown category_id/i.test(message)) {
    return `Der lokale Kaufland-Katalog für ${storefront.toUpperCase()} kennt diese Kategorie nicht. `
      + 'Bitte in den Einstellungen unter der Storefront-Kachel „Kategorien aktualisieren" ausführen und danach erneut zuordnen.';
  }
  return `Kategorie konnte nicht aufgenommen werden: ${message}`;
}

/** Fragetext vor der Aufnahme. Name kann fehlen (Vorschlagsdienst liefert ihn nicht immer). */
export function uebernahmeFrage(args: { kategorieName: string | null; kategorieId: number; storefront: string }): string {
  const name = args.kategorieName && args.kategorieName.trim().length > 0
    ? `„${args.kategorieName.trim()}" (Kaufland-ID ${args.kategorieId})`
    : `Kaufland-ID ${args.kategorieId}`;
  const sf = args.storefront.trim().toUpperCase();
  return `Die Kategorie ${name} steht noch nicht in deiner Kaufland-Auswahl für ${sf}. `
    + `Soll sie aufgenommen und in den Vario-Kategoriebaum übernommen werden?`;
}
